/**
 * Incremental record counters for streamed export bodies.
 *
 * An export streams its body and announces, in `X-Delopay-Record-Count`, how
 * many records that body will hold. These count the records actually received,
 * chunk by chunk, so a download can show `receivedRecords` of
 * `expectedRecords` while it runs and the finished file can be held to the
 * announced figure.
 *
 * They work on **bytes**, never on decoded text. Every character they look at —
 * `"` `\` `{` `}` `[` `]` and the line break — is ASCII, and no byte of a
 * multi-byte UTF-8 sequence falls in the ASCII range, so a chunk boundary can
 * land anywhere, the middle of a character included, without a decoder having
 * to carry a partial sequence across it.
 */

const LF = 0x0a;
const CR = 0x0d;
const QUOTE = 0x22;
const BACKSLASH = 0x5c;
const OPEN_BRACE = 0x7b;
const CLOSE_BRACE = 0x7d;
const OPEN_BRACKET = 0x5b;
const CLOSE_BRACKET = 0x5d;

/** Counts the records in a body fed to it one chunk at a time. */
export interface RecordCounter {
  /** Feed the next chunk of the body, in order. */
  push(chunk: Uint8Array): void;
  /** Records complete in what has been fed so far. */
  readonly records: number;
  /**
   * Whether what has been fed ends where a whole export body ends. A body
   * that stops anywhere else was cut off, whatever its record count says.
   */
  readonly complete: boolean;
  /** Records in the body, once it has ended. Does not change the counter. */
  finish(): number;
}

/**
 * RFC 4180 CSV with a header line: one record per line, where a field holding
 * `,` `"` or a line break is quoted and a quote inside it is doubled.
 *
 * A newline inside quotes is part of a field, not the end of a record, so every
 * `"` toggles "inside quotes". A doubled `""` toggles twice and leaves it as it
 * was, which is exactly its meaning. Records are the newlines outside quotes,
 * less one for the header.
 */
export class CsvRecordCounter implements RecordCounter {
  private inQuotes = false;
  private newlines = 0;
  /** Whether anything but a line break has arrived since the last newline. */
  private lineHasContent = false;

  push(chunk: Uint8Array): void {
    let inQuotes = this.inQuotes;
    let newlines = this.newlines;
    let lineHasContent = this.lineHasContent;
    for (const byte of chunk) {
      if (byte === QUOTE) {
        inQuotes = !inQuotes;
        lineHasContent = true;
      } else if (inQuotes) {
        lineHasContent = true;
      } else if (byte === LF) {
        newlines++;
        lineHasContent = false;
      } else if (byte !== CR) {
        lineHasContent = true;
      }
    }
    this.inQuotes = inQuotes;
    this.newlines = newlines;
    this.lineHasContent = lineHasContent;
  }

  get records(): number {
    return Math.max(0, this.newlines - 1);
  }

  /**
   * The export writer ends every line — the header and each record — with a
   * newline, so a whole body ends on one, outside quotes. RFC 4180 lets a
   * final line break be omitted, but these bodies never omit it, and without
   * that rule a file cut part-way through its last unquoted field
   * (`pay_1,12` of `pay_1,123`) would count as one more whole record and
   * match the figure it was announced with.
   */
  get complete(): boolean {
    return this.newlines >= 1 && !this.inQuotes && !this.lineHasContent;
  }

  /**
   * The records that ended. An unterminated tail is not one of them — see
   * {@link complete}.
   */
  finish(): number {
    return this.records;
  }
}

/** Before the rows array, inside it, and past its end. */
const SEEKING = 0;
const COUNTING = 1;
const CLOSED = 2;
type ArrayState = typeof SEEKING | typeof COUNTING | typeof CLOSED;

/**
 * A JSON object whose rows are an array member — `{"records":[…],…}` or
 * `{"data":[…],…}`.
 *
 * Counts the elements of the **first** array that is a direct member of the
 * top-level object: each one is counted as it closes back to that array's
 * depth, so a record is counted once it has fully arrived, never on its first
 * byte. Counting stops when that array closes, so an array later in the
 * envelope (inside the echoed `filters`, say) is never mistaken for rows.
 *
 * Strings are tracked with their escapes, so a `}` `]` or `"` inside a value
 * is text, not structure. Elements are counted when an object or array closes;
 * a bare scalar element is not a record and is not counted.
 */
export class JsonArrayRecordCounter implements RecordCounter {
  private depth = 0;
  private inString = false;
  private escaped = false;
  private topIsObject = false;
  private state: ArrayState = SEEKING;
  private count = 0;

  push(chunk: Uint8Array): void {
    if (this.state === CLOSED) return;
    let { depth, inString, escaped, topIsObject, count } = this;
    let state: ArrayState = this.state;
    for (const byte of chunk) {
      if (inString) {
        if (escaped) escaped = false;
        else if (byte === BACKSLASH) escaped = true;
        else if (byte === QUOTE) inString = false;
        continue;
      }
      if (byte === QUOTE) {
        inString = true;
      } else if (byte === OPEN_BRACE || byte === OPEN_BRACKET) {
        if (depth === 0 && byte === OPEN_BRACE) topIsObject = true;
        if (byte === OPEN_BRACKET && depth === 1 && topIsObject && state === SEEKING) {
          state = COUNTING;
        }
        depth++;
      } else if (byte === CLOSE_BRACE || byte === CLOSE_BRACKET) {
        depth--;
        if (state === COUNTING) {
          // Depth 2 is the rows array's own level: a value closing back to it
          // is one complete row. Closing to depth 1 is the array itself ending.
          if (depth === 2) count++;
          else if (depth === 1) {
            state = CLOSED;
            break;
          }
        }
      }
    }
    this.depth = depth;
    this.inString = inString;
    this.escaped = escaped;
    this.topIsObject = topIsObject;
    this.state = state;
    this.count = count;
  }

  get records(): number {
    return this.count;
  }

  /**
   * Whether the rows array has closed. What follows it is checked by parsing
   * the whole document, which a cut-off body fails.
   */
  get complete(): boolean {
    return this.state === CLOSED;
  }

  finish(): number {
    return this.count;
  }
}
